import React from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { Button } from "primereact/button";
import styled from "styled-components";
import { SupClass } from "../api/models/supClass.models";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  CreatePackagePayload,
  Package,
  UpdatePackagePayload,
} from "../api/models/package.models";
import { RowValuesWithEcomodClass } from "../api/models/rowValues.models";
import { InputText } from "primereact/inputtext";
import AddMaterialInputs from "../components/addMaterialInputs";
import SupSelection from "../components/supSelection";
import {
  LoadingContainer,
  HttpRequestStatus,
} from "../components/loadingContainer";
import {
  convertEcomodRowsToAkeneoRows,
  getDefaultValuesFromPackageValues,
  getTotalPrice,
} from "../utils/rowUtils";
import usePackageRow from "../hooks/usePackageRow";
import useFormUtils from "../utils/formUtils";
import { Family } from "../api/models/fullProductData.models";
import { v4 as uuidv4 } from "uuid";
import {
  CreatePackageRowPayload,
  UpdatePackageRowPayload,
} from "../api/models/packageRow.models";
import usePackages from "../hooks/usePackages";
import { SearchProduct } from "../api/models/searchProduct.models";
import { Price } from "../api/models/prices.models";
import { InfoDot } from "../components/infoDot";
import PriceYearSelector, {
  PriceYearNote,
} from "../components/priceYearSelector";
import { BasicProductInfo } from "../hooks/useMaterials";

const SectionHeader = styled.h4`
  font-weight: 500;
  font-size: 20px;
  letter-spacing: -0.4px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  gap: 0.25rem;

  & .p-inputtext,
  & .p-inputnumber {
    width: 100%;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;

  & > div {
    flex: 1;
    min-width: 200px;
  }
`;

const StickyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  width: 100%;
  bottom: 0;
  background-color: #fff;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
`;

const Section = styled.section`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

type PackageDialogInputs = {
  name: string;
  reportId: number;
  values: RowValuesWithEcomodClass[];
  comment?: string;
  productNumber?: string;
};

interface CreateAkeneoPackageFormProps {
  supClasses?: SupClass[];
  ecomodClasses?: EcomodClass[];
  selectedSearchProduct?: SearchProduct | null;
  onHide: () => void;
  packageData?: Package;
  refetchSearchProducts?: () => void;
  initialBasicInfo?: BasicProductInfo;
  onBasicInfoChange?: (values: BasicProductInfo) => void;
  pricesData?: Price[];
  priceYearOverride?: number | null;
  setPriceYearOverride?: (year: number | null) => void;
  priceYearOptions?: { label: string; value: number }[];
  priceDefaultYear?: number;
  isLoadingPrices?: boolean;
}

const CreateAkeneoPackageForm: React.FC<CreateAkeneoPackageFormProps> = ({
  supClasses,
  ecomodClasses,
  selectedSearchProduct,
  onHide,
  packageData,
  refetchSearchProducts,
  initialBasicInfo,
  onBasicInfoChange,
  pricesData,
  priceYearOverride,
  setPriceYearOverride,
  priceYearOptions = [],
  priceDefaultYear,
  isLoadingPrices = false,
}) => {
  const [companyId] = useUIStore(
    useShallow((state: UIState) => [state.companyId]),
  );
  const { t } = useTranslation();

  // Load package rows
  const {
    packageRow,
    isLoadingPackageRow,
    updateExistingPackageRow,
    isUpdatingPackageRow,
    createNewPackageRow,
    isCreatingPackageRow,
  } = usePackageRow({
    packageRowIdentifier: packageData?.packageRowsAssociation || null,
  });

  const {
    createNewPackage,
    isCreatingPackage,
    updateExistingPackage,
    isUpdatingPackage,
  } = usePackages({
    companyId: undefined,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
    watch,
  } = useForm<PackageDialogInputs>({
    defaultValues: {
      name: initialBasicInfo?.name ?? (selectedSearchProduct?.name || ""),
      comment: initialBasicInfo?.comment ?? (packageData?.comment || ""),
      productNumber:
        initialBasicInfo?.productNumber ??
        (packageData?.internalReference || ""),
    },
    mode: "onBlur",
  });

  const [isSup, setIsSup] = React.useState(false);
  const [supClassCode, setSupClassCode] = React.useState<string | undefined>(
    undefined,
  );

  const materialDefaultValues = React.useMemo(() => {
    return getDefaultValuesFromPackageValues(
      packageRow?.rowValues || [],
      ecomodClasses || [],
      supClasses || [],
    );
  }, [packageRow, ecomodClasses, supClasses]);

  React.useEffect(() => {
    if (materialDefaultValues.length > 0) {
      setIsSup(!!materialDefaultValues[0].isSup);
      setSupClassCode(materialDefaultValues[0].supClass);
    }
  }, [materialDefaultValues]);

  const watchName = watch("name");
  const watchProductNumber = watch("productNumber");
  const watchComment = watch("comment");

  React.useEffect(() => {
    onBasicInfoChange?.({
      name: watchName,
      ean: initialBasicInfo?.ean ?? "",
      productNumber: watchProductNumber ?? "",
      responsibleToReport: initialBasicInfo?.responsibleToReport ?? true,
      comment: watchComment,
    });
  }, [
    watchName,
    watchProductNumber,
    watchComment,
    initialBasicInfo?.ean,
    initialBasicInfo?.responsibleToReport,
    onBasicInfoChange,
  ]);

  const handleCreateNew = async (data: PackageDialogInputs) => {
    const packageRowIdentifier = uuidv4();

    // if user has selected materials, create package row first,
    // else just create product and attach existing package identifier as packageAssociation
    const packageRowPayload: CreatePackageRowPayload = {
      identifier: packageRowIdentifier,
      companyId: (companyId as number).toString(),
      name: data.name,
      family: Family.package_rows,
      synkkaImported: false,
      rowValues: convertEcomodRowsToAkeneoRows(data.values || []),
    };

    await createNewPackageRow(packageRowPayload);

    const payload: CreatePackagePayload = {
      identifier: uuidv4(),
      name: data.name,
      companyId: (companyId as number).toString(),
      family: Family.product,
      packageRowsAssociation: packageRowIdentifier,
      comment: data.comment,
      internalReference: data.productNumber || "",
    };

    const result = await createNewPackage(payload);

    refetchSearchProducts && refetchSearchProducts();

    if (!!result) {
      // Reset form and close dialog
      setValue("values", []);
      onHide();
    }
  };

  const handleUpdateExisting = async (data: PackageDialogInputs) => {
    if (!selectedSearchProduct) return;

    let packageRowsIdentifier = packageRow?.identifier || null;

    if (packageRowsIdentifier) {
      // Update existing package row:
      const packageRowPayload: UpdatePackageRowPayload = {
        identifier: packageRowsIdentifier as string,
        companyId: (companyId as number).toString(),
        name: data.name,
        family: Family.package_rows,
        synkkaImported: false,
        rowValues: convertEcomodRowsToAkeneoRows(data.values || []),
      };
      await updateExistingPackageRow(packageRowPayload);
    } else {
      // Create new package row and attach to product
      packageRowsIdentifier = uuidv4();
      const packageRowPayload: CreatePackageRowPayload = {
        identifier: packageRowsIdentifier,
        companyId: (companyId as number).toString(),
        name: data.name,
        family: Family.package_rows,
        synkkaImported: false,
        rowValues: convertEcomodRowsToAkeneoRows(data.values || []),
      };
      await createNewPackageRow(packageRowPayload);
    }

    // Update package with new package row association
    const productPayload: UpdatePackagePayload = {
      identifier: selectedSearchProduct.identifier,
      name: data.name,
      family: Family.package,
      packageRowsAssociation: packageRowsIdentifier,
      companyId: (companyId as number).toString(),
      comment: data.comment,
      internalReference: data.productNumber || "",
    };

    const result = await updateExistingPackage(productPayload);

    refetchSearchProducts && refetchSearchProducts();

    if (!!result) {
      // Reset form and close dialog
      setValue("values", []);
      onHide();
    }
  };

  const onSubmit = async (data: PackageDialogInputs) => {
    if (selectedSearchProduct) {
      await handleUpdateExisting(data);
    } else {
      await handleCreateNew(data);
    }
  };

  const rowValues = watch("values");

  const totalPrice = pricesData
    ? getTotalPrice(0, pricesData, rowValues)
    : null;

  const roundedPrice =
    totalPrice !== null
      ? parseFloat(totalPrice.toFixed(4)).toString() + " €"
      : "-";

  const saveDisabled = () => {
    return (
      rowValues?.length === 0 ||
      rowValues?.some((value) => !value.ecoModulationClass) ||
      rowValues?.some((value) => value.businessUse === undefined) ||
      rowValues?.some((value) => value.value === 0) ||
      !getValues("name") ||
      rowValues?.some(
        (value) =>
          !!value.secMaterial &&
          value.secValueType !== 2 &&
          (value.secValue ?? 0) > (value.value ?? 0),
      )
    );
  };

  const { getFormErrorMessage } = useFormUtils();

  return (
    <LoadingContainer
      status={
        isLoadingPackageRow
          ? HttpRequestStatus.LOADING
          : HttpRequestStatus.SUCCESS
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-container">
          <Section>
            <SectionHeader>{t("header-product-information")}</SectionHeader>

            <InputContainer>
              <label htmlFor="name">{t("label-product-name")}</label>
              <Controller
                name="name"
                control={control}
                rules={{ required: t("label-required") }}
                render={({ field }) => <InputText id="name" {...field} />}
              />
              {getFormErrorMessage(errors, "name")}
            </InputContainer>
            <Row>
              <InputContainer>
                <InfoDot
                  position="right"
                  text={t("infotext-product-number")}
                  marginLeft="10px"
                >
                  <label htmlFor="productNumber">
                    {t("label-product-number")}
                  </label>
                </InfoDot>

                <Controller
                  name="productNumber"
                  control={control}
                  render={({ field }) => (
                    <InputText id="productNumber" {...field} />
                  )}
                />
                {getFormErrorMessage(errors, "productNumber")}
              </InputContainer>
              <InputContainer>
                <label htmlFor="comment">{t("label-comment")}</label>
                <Controller
                  name="comment"
                  control={control}
                  render={({ field }) => <InputText id="comment" {...field} />}
                />
              </InputContainer>
            </Row>

            <SupSelection
              isSup={isSup}
              onIsSupChange={setIsSup}
              supClass={supClassCode}
              onSupClassChange={setSupClassCode}
              supClasses={supClasses}
            />
          </Section>

          <Section>
            <SectionHeader className="material-header">
              {t("label-add-material")}
            </SectionHeader>

            <AddMaterialInputs
              setReportRowValues={(values) => setValue("values", values)}
              ecomodClasses={ecomodClasses}
              defaultValues={getDefaultValuesFromPackageValues(
                packageRow?.rowValues || [],
                ecomodClasses || [],
                supClasses || [],
              )}
              supClasses={supClasses}
              isSup={isSup}
              supClass={supClassCode}
            />
          </Section>

          <Section>
            <LoadingContainer
              status={
                isLoadingPrices
                  ? HttpRequestStatus.LOADING
                  : HttpRequestStatus.SUCCESS
              }
            >
              <SectionHeader>{t("header-combined-prices")}</SectionHeader>
              <div>
                {!!roundedPrice && rowValues?.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span>
                      {t("label-combined-price-per-package-unit")}:{" "}
                      {roundedPrice} {t("label-vat-amount")}
                      {priceYearOverride && (
                        <PriceYearNote>
                          {`(${priceYearOverride} ${t("label-prices-from-year")})`}
                        </PriceYearNote>
                      )}
                    </span>
                    {setPriceYearOverride && (
                      <PriceYearSelector
                        priceYearOverride={priceYearOverride ?? null}
                        setPriceYearOverride={setPriceYearOverride}
                        priceYearOptions={priceYearOptions}
                        defaultYear={
                          priceDefaultYear ?? new Date().getFullYear()
                        }
                      />
                    )}
                  </div>
                )}
              </div>
            </LoadingContainer>
          </Section>
        </div>

        <StickyActions>
          <Button
            role="cancel"
            type="button"
            label={t("cmd-cancel")}
            className="p-button-text"
            onClick={onHide}
            disabled={
              isCreatingPackageRow ||
              isUpdatingPackageRow ||
              isCreatingPackage ||
              isUpdatingPackage
            }
          />
          <Button
            style={{ marginRight: "4rem" }}
            type="submit"
            label={t("cmd-save")}
            className="p-ml-2"
            disabled={saveDisabled()}
            loading={
              isCreatingPackageRow ||
              isUpdatingPackageRow ||
              isCreatingPackage ||
              isUpdatingPackage
            }
          />
        </StickyActions>
      </form>
    </LoadingContainer>
  );
};

export default CreateAkeneoPackageForm;
